function trim(value) {
  return typeof value === "string" ? value.trim() : ""
}

const SPONSORSHIP_PACKAGES = Object.freeze({
  site: Object.freeze({ key: "site", name: "Open Racer Partner", monthlyPriceEur: 499 }),
  discovery: Object.freeze({ key: "discovery", name: "Track Discovery Partner", monthlyPriceEur: 199 }),
})

function validEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
}

function validWebsite(value) {
  if (!value) return true
  try {
    const url = new URL(value)
    return url.protocol === "http:" || url.protocol === "https:"
  } catch (_) {
    return false
  }
}

function validSafeComponent(value) {
  return /^[A-Za-z0-9._-]+$/.test(value)
}

function sponsorshipRequestId(uid) {
  const safeUid = trim(uid)
  return validSafeComponent(safeUid) ? safeUid : ""
}

function rankSponsorshipCities(cities = [], query = "", limit = 8) {
  const term = trim(query).toLocaleLowerCase()
  const boundedLimit = Math.max(1, Math.min(Number(limit) || 8, 12))
  const canonicalCities = new Map()
  cities.forEach(city => {
    const filename = trim(city?.filename)
    if (!/^cities\/[a-z0-9]+(?:-[a-z0-9]+)*$/.test(filename)) return
    const existing = canonicalCities.get(filename)
    if (!existing || (Number(city.population) || 0) > (Number(existing.population) || 0)) {
      canonicalCities.set(filename, city)
    }
  })
  return [...canonicalCities.values()]
    .map(city => {
      const cityName = trim(city.city)
      const country = trim(city.country)
      const cityKey = cityName.toLocaleLowerCase()
      const label = [cityName, country].filter(Boolean).join(", ")
      const searchable = label.toLocaleLowerCase()
      let score = 4
      if (!term || cityKey === term) score = 0
      else if (cityKey.startsWith(term)) score = 1
      else if (searchable.startsWith(term)) score = 2
      else if (searchable.includes(term)) score = 3
      return { ...city, city: cityName, country, label, score }
    })
    .filter(city => !term || city.score < 4)
    .sort((left, right) => left.score - right.score
      || (Number(right.population) || 0) - (Number(left.population) || 0)
      || left.label.localeCompare(right.label))
    .slice(0, boundedLimit)
    .map(({ score, ...city }) => city)
}

function matchDetectedCity(cities, name, latitude, longitude) {
  const detectedName = trim(name).toLocaleLowerCase()
  if (!detectedName) return null

  const canonical = new Map()
  cities.forEach(city => {
    const filename = trim(city?.filename)
    if (/^cities\/[a-z0-9]+(?:-[a-z0-9]+)*$/.test(filename) && !canonical.has(filename)) {
      canonical.set(filename, city)
    }
  })
  const candidates = [...canonical.values()]
  const withLabel = city => ({
    ...city,
    label: [trim(city.city), trim(city.country)].filter(Boolean).join(", "),
  })
  const exactLabelMatches = candidates.filter(city => (
    [trim(city.city), trim(city.country)].filter(Boolean).join(", ").toLocaleLowerCase() === detectedName
  ))
  if (exactLabelMatches.length === 1) return withLabel(exactLabelMatches[0])

  const exactCityMatches = candidates.filter(city => trim(city?.city).toLocaleLowerCase() === detectedName)
  if (exactCityMatches.length === 1) return withLabel(exactCityMatches[0])
  if (exactCityMatches.length < 2) return null

  const lat = Number(latitude)
  const lng = Number(longitude)
  const hasCoordinateValues = latitude !== null && latitude !== undefined && String(latitude).trim() !== ""
    && longitude !== null && longitude !== undefined && String(longitude).trim() !== ""
  const hasCoordinates = hasCoordinateValues && Number.isFinite(lat) && Number.isFinite(lng)
    && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180
  if (!hasCoordinates) return null

  const locatedMatches = exactCityMatches.filter(city => {
    const cityLat = Number(city?.lat)
    const cityLng = Number(city?.lng)
    return city?.lat !== null && city?.lat !== undefined && String(city.lat).trim() !== ""
      && city?.lng !== null && city?.lng !== undefined && String(city.lng).trim() !== ""
      && Number.isFinite(cityLat) && Number.isFinite(cityLng)
      && cityLat >= -90 && cityLat <= 90 && cityLng >= -180 && cityLng <= 180
  })
  if (!locatedMatches.length) return null
  const longitudeScale = Math.cos(lat * Math.PI / 180)
  locatedMatches.sort((left, right) => {
    const leftDistance = (Number(left.lat) - lat) ** 2 + ((Number(left.lng) - lng) * longitudeScale) ** 2
    const rightDistance = (Number(right.lat) - lat) ** 2 + ((Number(right.lng) - lng) * longitudeScale) ** 2
    return leftDistance - rightDistance
  })
  return withLabel(locatedMatches[0])
}

async function detectSponsorshipCity(cities = [], {
  storage,
  fetcher,
  signal,
  timeoutMs = 5000,
} = {}) {
  let browserStorage = storage || null
  let browserFetch = fetcher || null
  if (typeof window !== "undefined") {
    if (!browserStorage) {
      try { browserStorage = window.sessionStorage } catch (_) { browserStorage = null }
    }
    if (!browserFetch) {
      try { browserFetch = typeof window.fetch === "function" ? window.fetch.bind(window) : null } catch (_) { browserFetch = null }
    }
  }
  const safeGet = key => {
    try { return browserStorage?.getItem(key) || "" } catch (_) { return "" }
  }
  const safeSet = (key, value) => {
    try { browserStorage?.setItem(key, String(value)) } catch (_) {}
  }
  const storedLocation = safeGet("locationname")
  const storedCity = safeGet("city")
  const storedQuery = storedLocation || storedCity
  const storedMatch = storedQuery ? matchDetectedCity(
    cities,
    storedQuery,
    safeGet("latitude"),
    safeGet("longitude"),
  ) : null
  if (storedMatch) return storedMatch
  if (!browserFetch || signal?.aborted) return null

  const controller = typeof AbortController !== "undefined" ? new AbortController() : null
  const boundedTimeout = Math.max(1, Math.min(Number(timeoutMs) || 5000, 10000))
  let timeoutId
  let detachAbort
  try {
    const aborted = new Promise((_, reject) => {
      const rejectAbort = () => {
        controller?.abort()
        reject(new Error("Location detection aborted"))
      }
      if (signal) {
        signal.addEventListener("abort", rejectAbort, { once: true })
        detachAbort = () => signal.removeEventListener("abort", rejectAbort)
      }
      timeoutId = setTimeout(() => {
        controller?.abort()
        reject(new Error("Location detection timed out"))
      }, boundedTimeout)
    })
    const response = await Promise.race([
      browserFetch("https://or.ghe.workers.dev", { signal: controller?.signal }),
      aborted,
    ])
    if (!response.ok) return null
    const geo = await response.json()
    const detectedName = trim(geo?.city)
    if (!detectedName || detectedName.length > 200) return null
    const detected = matchDetectedCity(cities, detectedName, geo.latitude, geo.longitude)
    if (!detected || detected.city.toLocaleLowerCase() !== detectedName.toLocaleLowerCase()) return null
    safeSet("city", detected.city)
    safeSet("locationname", detected.label)
    if (geo.latitude !== undefined) safeSet("latitude", geo.latitude)
    if (geo.longitude !== undefined) safeSet("longitude", geo.longitude)
    return detected
  } catch (_) {
    return null
  } finally {
    clearTimeout(timeoutId)
    detachAbort?.()
    controller?.abort()
  }
}

function sponsorshipPlacementFromLocation(location, cities = []) {
  const params = new URLSearchParams(location?.search || "")
  if (params.get("package") === "discovery") {
    return {
      packageKey: "discovery",
      targetKey: "track-discovery",
      targetName: "Track discovery",
      targetPath: "/go-karting-tracks",
    }
  }
  return {
    packageKey: "site",
    targetKey: "site",
    targetName: "Open Racer",
    targetPath: "/",
  }
}

function sponsorshipRequestPath({ packageKey } = {}) {
  return packageKey === "discovery" ? "/sponsorship?package=discovery" : "/sponsorship"
}

function validPlacement(input = {}) {
  const packageKey = trim(input.packageKey)
  const targetKey = trim(input.targetKey)
  const targetName = trim(input.targetName)
  const targetPath = trim(input.targetPath)

  if (!SPONSORSHIP_PACKAGES[packageKey]) return false
  if (packageKey === "site") {
    return targetKey === "site" && targetName === "Open Racer" && targetPath === "/"
  }
  return packageKey === "discovery"
    && targetKey === "track-discovery"
    && targetName === "Track discovery"
    && targetPath === "/go-karting-tracks"
}

function validateSponsorshipRequest(input = {}) {
  const errors = {}
  const uid = trim(input.uid)
  const contactName = trim(input.contactName)
  const companyName = trim(input.companyName)
  const role = trim(input.role)
  const businessEmail = trim(input.businessEmail).toLowerCase()
  const businessWebsite = trim(input.businessWebsite)
  const campaignSummary = trim(input.campaignSummary)

  if (!validPlacement(input) || !uid || !validSafeComponent(uid)) {
    errors.form = "Choose a valid sponsorship placement."
  }

  if (!contactName) errors.contactName = "Enter your full name."
  else if (contactName.length > 120) errors.contactName = "Name must be 120 characters or fewer."

  if (!companyName) errors.companyName = "Enter your company name."
  else if (companyName.length > 160) errors.companyName = "Company name must be 160 characters or fewer."

  if (!role) errors.role = "Tell us your role at the company."
  else if (role.length > 120) errors.role = "Role must be 120 characters or fewer."
  if (!businessEmail) errors.businessEmail = "Business email is required."
  else if (businessEmail.length > 254) errors.businessEmail = "Business email must be 254 characters or fewer."
  else if (!validEmail(businessEmail)) errors.businessEmail = "Enter a valid business email."
  if (businessWebsite.length > 500) errors.businessWebsite = "Website must be 500 characters or fewer."
  else if (!validWebsite(businessWebsite)) errors.businessWebsite = "Enter a valid http or https website."
  if (campaignSummary.length > 2000) errors.campaignSummary = "Campaign summary must be 2,000 characters or fewer."

  return errors
}

function buildSponsorshipRequest(input, timestamp) {
  const errors = validateSponsorshipRequest(input)
  if (Object.keys(errors).length) {
    const error = new Error("Sponsorship request is invalid")
    error.fields = errors
    throw error
  }

  const sponsorshipPackage = SPONSORSHIP_PACKAGES[trim(input.packageKey)]
  const targetKey = trim(input.targetKey)
  const targetSlug = ""
  return {
    packageKey: sponsorshipPackage.key,
    packageName: sponsorshipPackage.name,
    monthlyPriceEur: sponsorshipPackage.monthlyPriceEur,
    targetKey,
    targetSlug,
    targetName: trim(input.targetName),
    targetPath: trim(input.targetPath),
    uid: trim(input.uid),
    accountEmail: trim(input.accountEmail),
    contactName: trim(input.contactName),
    companyName: trim(input.companyName),
    role: trim(input.role),
    businessEmail: trim(input.businessEmail).toLowerCase(),
    businessWebsite: trim(input.businessWebsite),
    campaignSummary: trim(input.campaignSummary),
    status: "pending",
    createdAt: timestamp,
    updatedAt: timestamp,
  }
}

module.exports = {
  SPONSORSHIP_PACKAGES,
  buildSponsorshipRequest,
  detectSponsorshipCity,
  rankSponsorshipCities,
  sponsorshipPlacementFromLocation,
  sponsorshipRequestId,
  sponsorshipRequestPath,
  validateSponsorshipRequest,
}
